(() => {
  'use strict';

  const STATE = {
    quantity: null,
    lastInteractionAt: 0,
  };

  const BUY_QUANTITY_PATTERN = /\bbuy\s*(\d+)\b/gi;
  const PUMPER_MARKER_PATTERN = /pumper|profit[\s_-]*pumper/i;

  const toPositiveInteger = value => {
    const quantity = Number.parseInt(String(value || ''), 10);
    return Number.isInteger(quantity) && quantity > 0 ? quantity : null;
  };

  const quantitiesFromText = text => {
    const quantities = new Set();
    const normalizedText = String(text || '');
    let match;

    BUY_QUANTITY_PATTERN.lastIndex = 0;
    while ((match = BUY_QUANTITY_PATTERN.exec(normalizedText)) !== null) {
      const quantity = toPositiveInteger(match[1]);
      if (quantity) quantities.add(quantity);
    }

    return [...quantities];
  };

  const uniqueQuantityFromElement = element => {
    if (!(element instanceof Element)) return null;

    const dataQuantity =
      element.dataset.quantity ||
      element.dataset.qty ||
      element.dataset.totalQuantity;
    const quantityFromData = toPositiveInteger(dataQuantity);
    if (quantityFromData) return quantityFromData;

    const quantities = quantitiesFromText(element.textContent);
    return quantities.length === 1 ? quantities[0] : null;
  };

  const looksLikePumperRoot = element => {
    if (!(element instanceof Element)) return false;

    const marker = [
      element.id,
      element.className,
      element.getAttribute('data-block-handle'),
      element.getAttribute('data-app'),
      element.getAttribute('data-app-name'),
    ]
      .filter(Boolean)
      .join(' ');

    const quantities = quantitiesFromText(element.textContent);
    const hasTierList = quantities.length >= 2 && quantities.includes(1);

    return hasTierList && (
      PUMPER_MARKER_PATTERN.test(marker) ||
      element.matches('.shopify-app-block, [id^="shopify-block-"]')
    );
  };

  const findPumperRoots = () => {
    const selectors = [
      '.shopify-app-block',
      '[id^="shopify-block-"]',
      '[id*="pumper" i]',
      '[class*="pumper" i]',
      '[data-app*="pumper" i]',
      '[data-app-name*="pumper" i]',
    ];

    return [...document.querySelectorAll(selectors.join(','))].filter(
      looksLikePumperRoot
    );
  };

  const findPumperRootForTarget = target => {
    if (!(target instanceof Element)) return null;

    let element = target;
    while (element && element !== document.documentElement) {
      if (looksLikePumperRoot(element)) return element;
      element = element.parentElement;
    }

    return null;
  };

  const quantityFromOptionElement = (option, root) => {
    if (!(option instanceof Element) || !(root instanceof Element)) return null;

    let element = option;
    while (element && element !== root) {
      const quantity = uniqueQuantityFromElement(element);
      if (quantity) return quantity;
      element = element.parentElement;
    }

    return null;
  };

  const inferSelectedQuantity = () => {
    const roots = findPumperRoots();

    for (const root of roots) {
      const selectedOptions = root.querySelectorAll(
        [
          'input[type="radio"]:checked',
          'input[type="checkbox"]:checked',
          '[aria-checked="true"]',
          '[aria-selected="true"]',
          '[data-selected="true"]',
          '[class*="selected" i]',
          '[class*="active" i]',
        ].join(',')
      );

      for (const option of selectedOptions) {
        const quantity = quantityFromOptionElement(option, root);
        if (quantity) return quantity;
      }
    }

    return roots.length ? 1 : null;
  };

  const rememberQuantityFromTarget = target => {
    const root = findPumperRootForTarget(target);
    if (!root) return;

    const quantity = quantityFromOptionElement(target, root);
    if (!quantity) return;

    STATE.quantity = quantity;
    STATE.lastInteractionAt = Date.now();
  };

  const getSelectedQuantity = () => {
    const recentlySelected =
      STATE.quantity && Date.now() - STATE.lastInteractionAt < 2000;

    if (recentlySelected) return STATE.quantity;

    const inferredQuantity = inferSelectedQuantity();
    if (inferredQuantity) STATE.quantity = inferredQuantity;

    return inferredQuantity || STATE.quantity;
  };

  const associatedQuantityControls = form => {
    const controls = [...form.querySelectorAll('[name="quantity"]')];

    if (form.id) {
      const escapedFormId = window.CSS && CSS.escape
        ? CSS.escape(form.id)
        : form.id.replace(/(["\\])/g, '\\$1');

      controls.push(
        ...document.querySelectorAll(
          `[name="quantity"][form="${escapedFormId}"]`
        )
      );
    }

    return [...new Set(controls)];
  };

  const syncForm = form => {
    if (!(form instanceof HTMLFormElement)) return null;

    const quantity = getSelectedQuantity();
    if (!quantity) return null;

    const controls = associatedQuantityControls(form);
    let bridgeInput = form.querySelector(
      'input[data-ovelis-pumper-quantity="true"]'
    );

    for (const control of controls) {
      if (
        control instanceof HTMLInputElement &&
        (control.type === 'radio' || control.type === 'checkbox') &&
        !control.checked
      ) {
        continue;
      }

      control.value = String(quantity);
    }

    if (!bridgeInput) {
      bridgeInput = document.createElement('input');
      bridgeInput.type = 'hidden';
      bridgeInput.name = 'quantity';
      bridgeInput.dataset.ovelisPumperQuantity = 'true';
      form.appendChild(bridgeInput);
    }

    bridgeInput.value = String(quantity);
    form.dataset.ovelisPumperQuantity = String(quantity);

    return quantity;
  };

  const formForButton = button => {
    if (!(button instanceof Element)) return null;

    const productForm = button.closest('product-form');
    return productForm ? productForm.querySelector('form') : button.form;
  };

  document.addEventListener(
    'pointerdown',
    event => rememberQuantityFromTarget(event.target),
    true
  );

  document.addEventListener(
    'change',
    event => rememberQuantityFromTarget(event.target),
    true
  );

  document.addEventListener(
    'click',
    event => {
      rememberQuantityFromTarget(event.target);

      const button = event.target.closest(
        '.main-product-atc, [data-skip-cart-button="true"]'
      );
      const form = formForButton(button);
      if (form) syncForm(form);
    },
    true
  );

  document.addEventListener(
    'submit',
    event => {
      const form = event.target;
      if (
        form instanceof HTMLFormElement &&
        form.matches('[data-type="add-to-cart-form"]')
      ) {
        syncForm(form);
      }
    },
    true
  );

  window.OvelisPumperQuantityBridge = Object.freeze({
    getSelectedQuantity,
    syncForm,
  });
})();
